"use client";

import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@/app/hooks/useRouter";
import dynamic from "next/dynamic";
import MobileRegisterForm from "@/app/components/auth/Forms/MobileRegisterForm";
import OtpRegisterForm from "@/app/components/auth/Forms/OtpRegisterForm";
import IdentityRegisterForm from "@/app/components/auth/Forms/IdentityRegisterForm";
import PasswordRegisterForm from "@/app/components/auth/Forms/PasswordRegisterForm";
import { requestPostLoginAccountSelection } from "@/app/components/auth/PostLoginAccountSelector";
import PasswordForm from "@/app/components/auth/Forms/passwordForm";
import AuthenticatorRecoveryForm from "@/app/components/Dashboard/Security/TwoFactorAuthentication/v3/AuthenticatorRecoveryForm";
import { encode } from "base-64";
import { useTranslations } from "next-intl";
import { getOtpInputErrorKey } from "@/app/components/UI/VerificationCode/otpErrors";
import { useFeatureStatus } from "@/app/context/FeatureStatusContext";
import { sendGTMEvent } from "@/app/utils/analytics";
import { hasEnterpriseLabel } from "@/app/utils/enterpriseToken";
import useSetCookieAsync from "@/app/utils/setCookie";
import { useAuth } from "@/app/context/AuthContext";
import { useNotification } from "@/app/context/NotificationContext";
import { useAuthAccounts, useAuthCaptcha, useAuthIdentity, useAuthLogin, useAuthMobile, useAuthOtp, useAuthPasswordRegistration } from "@/app/apiServices/auth/service";
import type { AuthMobileResponse, AuthOtpResponse } from "@/app/apiServices/auth/types";
import toast from "react-hot-toast";
import { showSuccessToast } from "@/app/utils/toast-notif";
import { useLoginRecovery } from "@/app/apiServices/security/twoFactorFlows";
import type { LoginRecoveryTokens } from "@/app/components/Dashboard/Security/security.interface";
import type { AccountSwitchTarget } from "@/app/context/AuthContext.interface";
const loadAccountSelectorModal = () => import("@/app/components/auth/Modals/AccountSelectorModal");
const loadAuthWarningModal = () => import("@/app/components/auth/Modals/AuthWarningModal");
const loadAuthNoticeModal = () => import("@/app/components/auth/Modals/AuthNoticeModal");
const AccountSelectorModal = dynamic(loadAccountSelectorModal, {
  ssr: false
});
const AuthWarningModal = dynamic(loadAuthWarningModal, {
  ssr: false
});
const AuthNoticeModal = dynamic(loadAuthNoticeModal, {
  ssr: false
});
export default function RegLoginComponent() {
  const t = useTranslations();
  const featureStatus = useFeatureStatus();
  const router = useRouter();
  const queryParams = useSearchParams();
  const referral = queryParams.get("referralCode");
  const mobileQuery = queryParams.get("mobile");
  const hasRestriction = featureStatus.restrictLogin;
  const {
    setCookieAsync
  } = useSetCookieAsync();
  const {
    refreshUser,
    user,
    switchAccount,
    decodedToken
  } = useAuth();
  const {
    checkNextNotification
  } = useNotification();
  const [title, setTile] = useState<string>("");
  const [step, setStep] = useState<number>(0);
  const [mobile, setMobile] = useState<string>(mobileQuery ?? "");
  const [password, setPassword] = useState<string>("");
  const [mobileResponse, setMobileResponse] = useState<AuthMobileResponse>({
    TFA: false,
    token: "",
    ttl: 0
  });
  const [otpResponse, setOtpResponse] = useState<AuthOtpResponse>({
    token: ""
  });
  const [otpTimer, setOtpTimer] = useState<NodeJS.Timeout | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountSwitchTarget | null>(null);
  const [warningModal, setWarningModal] = useState(false);
  const [pendingAccountModal, setPendingAccountModal] = useState(false);
  const [isCompletingLogin, setIsCompletingLogin] = useState(false);
  const lastOtpToastTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (hasRestriction) void loadAuthNoticeModal();
    if (pendingAccountModal) void loadAccountSelectorModal();
    if (warningModal) void loadAuthWarningModal();
  }, [hasRestriction, pendingAccountModal, warningModal]);
  const captchaQuery = useAuthCaptcha();
  const mobileMutation = useAuthMobile();
  const otpMutation = useAuthOtp();
  const passwordRegistrationMutation = useAuthPasswordRegistration();
  const identityMutation = useAuthIdentity();
  const loginMutation = useAuthLogin();
  const accountsQuery = useAuthAccounts(pendingAccountModal);
  const isEnterpriseRequestReady = !accountsQuery.isFetching && accountsQuery.isSuccess;
  const shouldOpenAccountModal = pendingAccountModal && isEnterpriseRequestReady;
  let flow: string;
  if (mobileMutation.isSuccess && mobileResponse.flow == "login") {
    flow = "login";
  } else flow = "register";
  useEffect(() => {
    if (referral) {
      localStorage.setItem("referralCode", referral);
    }
  }, [referral]);
  const storedReferral = referral || (typeof window !== "undefined" ? localStorage.getItem("referralCode") : null) || undefined;
  const performRedirect = async (notify = true) => {
    const redirectAfterLogin = localStorage.getItem("redirectAfterLogin");
    if (redirectAfterLogin) {
      await router.replace(redirectAfterLogin);
      localStorage.removeItem("redirectAfterLogin");
    } else {
      const redirectPath = localStorage.getItem("redirectPath");
      if (redirectPath) {
        await router.replace(redirectPath);
      } else {
        await router.replace("/");
      }
    }
    localStorage.removeItem("redirectPath");
    if (notify) checkNextNotification();
  };
  const handleAccountSelection = async (account: AccountSwitchTarget) => {
    try {
      if (account.accountType === "ENTERPRISE") {
        if (await switchAccount(account)) checkNextNotification();
      } else {
        await performRedirect();
      }
    } finally {
      setShowAccountModal(false);
    }
  };

  //step 0
  useEffect(() => {
    if (mobileMutation.isSuccess && mobileMutation.data?.message) {
      const response = mobileMutation.data.message;
      if (featureStatus.otp || flow == "register") setStep(1);else setStep(2);
      sendGTMEvent({
        event: "Phone-No-Fill",
        page: "auth",
        action: "trigger",
        parameter: "phone-no"
      });
      setMobileResponse(response);
    }
  }, [mobileMutation.isSuccess, mobileMutation.data, featureStatus.otp, flow]);
  useEffect(() => {
    const response_0 = mobileMutation.data?.message;
    if (mobileMutation.isSuccess && response_0?.TFA === "SMS" && featureStatus.otp && response_0.token && lastOtpToastTokenRef.current !== response_0.token) {
      // Read the flow from the response itself: the derived `flow` lags this effect by a
      // render because it depends on state set in the step-0 effect.
      toast.success(t(response_0.flow === "login" ? "auth.OTP_SENT_TO_YOU" : "auth.VERIFICATION_CODE_SENT_TO_YOU"));
      lastOtpToastTokenRef.current = response_0.token;
    }
  }, [mobileMutation.isSuccess, mobileMutation.data, featureStatus.otp, t]);

  //step 1
  useEffect(() => {
    if (otpMutation.isSuccess && otpMutation.data?.message) {
      if (flow == "login") {
        sendGTMEvent({
          event: "OTP-Done",
          page: "auth",
          action: "trigger"
        });
        setStep(2);
      } else {
        setStep(3);
        sendGTMEvent({
          event: "OTP-Done",
          page: "auth",
          action: "trigger"
        });
      }
      setOtpResponse(otpMutation.data.message);
    }
  }, [otpMutation.isSuccess, otpMutation.data, flow]);

  // Shared by password login and the Authenticator recovery login (PRD 8): store the session, then redirect.
  const completeLogin = async (token: string, refresh_token: string) => {
    setIsCompletingLogin(true);
    try {
      await Promise.all([setCookieAsync("auth-token", token), setCookieAsync("auth-refresh", refresh_token)]);
      // Pass the fresh token: refreshUser's closure still holds the pre-login cookie value.
      // Not awaited: the session cookies are already set, and a slow profile request must not block the redirect.
      void refreshUser(token);

      // The accounts request can be slow: redirect now, the root-level selector opens the picker when it arrives.
      // For enterprise users the notification check waits for the account selection (PostLoginAccountSelector).
      await performRedirect(!requestPostLoginAccountSelection(token));
    } catch (error) {
      console.error("Error setting cookies or redirecting:", error);
    } finally {
      setIsCompletingLogin(false);
    }
  };

  // step 2 (login password)
  useEffect(() => {
    if (flow == "login") {
      if (loginMutation.isSuccess && loginMutation.data?.message) {
        const {
          token: token_0,
          refresh_token: refresh_token_0
        } = loginMutation.data.message;
        void completeLogin(token_0, refresh_token_0);
      }
    }
  }, [loginMutation.isSuccess, loginMutation.data]);

  // step 5 (forgot Authenticator during login, PRD 8)
  const handleRecovered = async (tokens: LoginRecoveryTokens) => {
    showSuccessToast({
      title: t("security.TFA_TOAST_RECOVERED_TITLE"),
      text: t("security.TFA_TOAST_RECOVERED_TEXT")
    });
    await completeLogin(tokens.token, tokens.refresh_token);
  };
  const recovery = useLoginRecovery(mobile, mobileResponse.token, handleRecovered);
  const startRecovery = () => {
    setStep(5);
    recovery.start();
  };
  async function acceptMobileForm(mobileNumber: string, password_0: string) {
    if (flow == "login") {
      try {
        const encodePassword = encode(password_0);
        //api call for mobile number & password
        loginMutation.mutate({
          mobile: mobileNumber,
          password: encodePassword,
          tracking_id: otpResponse.token ?? otpResponse.tracking_id ?? mobileResponse.token
        });
        sendGTMEvent({
          event: "Final-Login",
          page: "auth",
          action: "trigger"
        });
      } catch (err) {
        //api call for mobile number & password (without encode password,because user type persian letter)
        loginMutation.mutate({
          mobile: mobileNumber,
          password: password_0,
          tracking_id: otpResponse.token ?? otpResponse.tracking_id ?? mobileResponse.token
        });
      }
      setMobile(mobileNumber);
      setPassword(password_0);
    }
  }

  //step 3 (register password)
  useEffect(() => {
    if (flow == "register") {
      if (passwordRegistrationMutation.isSuccess && passwordRegistrationMutation.data?.message) {
        setCookieAsync("auth-token", passwordRegistrationMutation.data.message.token);
        setCookieAsync("auth-refresh", passwordRegistrationMutation.data.message.refresh_token);
        setStep(4);
      }
    }
  }, [passwordRegistrationMutation.isSuccess, passwordRegistrationMutation.data]);
  useEffect(() => {
    if (!shouldOpenAccountModal) {
      return;
    }
    setPendingAccountModal(false);
    setShowAccountModal(true);
  }, [isEnterpriseRequestReady, pendingAccountModal, shouldOpenAccountModal]);
  useEffect(() => {
    if (step === 2 || step === 3) {
      const ttl = mobileResponse.ttl || 120;
      const timer = setTimeout(() => {
        setStep(0);
        // setMobile("")
      }, ttl * 1000);
      setOtpTimer(timer);
    } else {
      if (otpTimer) {
        clearTimeout(otpTimer);
        setOtpTimer(null);
      }
    }
  }, [step, mobileResponse.ttl]);
  useEffect(() => {
    if (!mobile) {
      setStep(0);
    }
  }, [mobile]);

  // The captcha is single-use and consumed by the step-0 submit, so returning to
  // step 0 (edit mobile, back, OTP timeout) must load a fresh one.
  const prevStepRef = useRef(step);
  const refetchCaptcha = captchaQuery.refetch;
  useEffect(() => {
    if (step === 0 && prevStepRef.current !== 0) void refetchCaptcha();
    prevStepRef.current = step;
  }, [step, refetchCaptcha]);
  function backFunc() {
    if (step == 0) return;
    setStep(step - 1);
  }
  const handleTitle = () => {
    if (step == 0 || step == 1) {
      setTile(t("auth.LOGIN_REGISTER_TEXT"));
    }
    if (step == 2 || step == 5) {
      setTile(t("auth.LOGIN_TEXT"));
    }
    if (step == 3 || step == 4) {
      setTile(t("auth.REGISTER_TEXT"));
    }
  };
  useEffect(() => {
    handleTitle();
  }, [step]);
  const closeWarning = () => {
    if (hasEnterpriseLabel(decodedToken?.labels)) {
      setShowAccountModal(true);
    } else {
      performRedirect();
    }
    setWarningModal(false);
  };
  return <div>
      {step === 0 && <MobileRegisterForm mobile={mobile} setMobile={setMobile} captcha={captchaQuery.data} isCaptchaLoading={captchaQuery.isFetching} isMobilePending={mobileMutation.isPending} isMobileError={mobileMutation.isError} onRefreshCaptcha={() => void captchaQuery.refetch()} onSubmitMobile={payload => mobileMutation.mutate(payload)} performRedirect={performRedirect} setWarningModal={setWarningModal} />}

      {step === 1 && <OtpRegisterForm provider={mobileResponse.TFA} mobile={mobile} backFunc={backFunc} setStep={setStep} setMobile={setMobile} registerMobileResponse={mobileResponse} trackingId={mobileResponse.token} onVerifyOtp={payload_0 => otpMutation.mutate(payload_0)} onResendOtp={tfaMethod => mobileMutation.mutate({
      mobile,
      tracking_id: mobileResponse.token,
      tfaMethod
    })} isMobilePending={mobileMutation.isPending} isOtpPending={otpMutation.isPending} isOtpError={otpMutation.isError} otpErrorMessage={otpMutation.isError ? t(getOtpInputErrorKey(otpMutation.error)) : undefined} flow={mobileResponse.flow} onNoAccess={featureStatus.twoFactorV3 && flow === "login" ? startRecovery : undefined} />}

      {step === 2 && <PasswordForm mobile={mobile} backFunc={backFunc} confirmFunction={acceptMobileForm} passwordStates={{
      isLoading: loginMutation.isPending || isCompletingLogin,
      isDone: loginMutation.isSuccess,
      hasError: loginMutation.isError
    }} />}

      {step === 5 && <AuthenticatorRecoveryForm phase={recovery.phase} mobile={mobile} code={recovery.code} onCodeChange={recovery.setCode} password={recovery.password} onPasswordChange={recovery.setPassword} expiresAt={recovery.expiresAt} errorKind={recovery.errorKind} isSubmitting={recovery.isSubmitting || isCompletingLogin} onSubmitOtp={recovery.submitOtp} onSubmitPassword={recovery.submitPassword} onResend={recovery.resendOtp} onCancel={() => setStep(1)} />}

      {step === 3 && <PasswordRegisterForm mobile={mobile} backFunc={backFunc} trackingId={otpResponse.tracking_id ?? otpResponse.token ?? mobileResponse.token} onSubmit={payload_1 => passwordRegistrationMutation.mutate(payload_1)} isPending={passwordRegistrationMutation.isPending} isSuccess={passwordRegistrationMutation.isSuccess} isError={passwordRegistrationMutation.isError} />}

      {step === 4 && <IdentityRegisterForm mobile={mobile} backFunc={backFunc} trackingId={passwordRegistrationMutation.variables?.tracking_id ?? otpResponse.tracking_id ?? otpResponse.token ?? mobileResponse.token} onSubmit={payload_2 => identityMutation.mutate(payload_2)} isPending={identityMutation.isPending} isSuccess={identityMutation.isSuccess} isError={identityMutation.isError} error={identityMutation.error} referralCode={storedReferral || undefined} />}

      {showAccountModal && <AccountSelectorModal isOpen={showAccountModal} onClose={() => setShowAccountModal(false)} onSelect={handleAccountSelection} selectedAccount={selectedAccount} setSelectedAccount={setSelectedAccount} accounts={accountsQuery.data?.message ?? []} />}

      {warningModal && <AuthWarningModal open={warningModal} onClose={closeWarning} />}

      {hasRestriction && <AuthNoticeModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </div>;
}